import React from 'react';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Task } from '../types/task';
import { useAuth } from '../contexts/AuthContext';
import DeleteTaskButton from './DeleteTaskButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { taskService } from '../services/taskService';

interface TaskActionsProps {
  task: Task;
  onTaskUpdated?: () => void;
}

const TaskActions: React.FC<TaskActionsProps> = ({ task, onTaskUpdated }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isCompleting, setIsCompleting] = React.useState(false);
  
  // Проверяваме дали текущият потребител е собственик на задачата
  const isOwner = user?.id && String(user.id) === String(task.user_id);
  
  if (!isOwner) {
    return null; // Не показваме панела с действия, ако потребителят не е собственик
  }

  const handleCompleteTask = async () => {
    const confirmed = window.confirm(
      `${t('tasks.actions.complete_confirmation', 'Сигурни ли сте, че искате да завършите задачата?')}\n\n${t(
        'tasks.actions.review_edit_note',
        'След публикуване отзивът може да се редактира или изтрива до 24 часа.'
      )}`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsCompleting(true);
      await taskService.completeTask(task.id);
      window.alert(
        `${t('tasks.actions.complete_success', 'Задачата е завършена успешно.')}\n\n${t(
          'tasks.actions.review_edit_note',
          'След публикуване отзивът може да се редактира или изтрива до 24 часа.'
        )}`
      );
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error completing task:', error);
      window.alert(t('tasks.actions.complete_error', 'Неуспешно завършване на задачата. Опитай отново.'));
    } finally {
      setIsCompleting(false);
    }
  };
  
  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('tasks.actions.title')}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {task.status === 'open' && (
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth
            onClick={() => navigate(`/tasks/${task.id}/edit`)}
          >
            {t('tasks.actions.edit_task')}
          </Button>
        )}

        {task.status === 'in_progress' && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleCompleteTask}
            disabled={isCompleting}
          >
            {isCompleting
              ? t('common.loading', 'Зареждане...')
              : t('tasks.actions.complete_task', 'Завърши задачата')}
          </Button>
        )}
        
        <DeleteTaskButton 
          taskId={task.id} 
          onDelete={() => {
            if (onTaskUpdated) {
              onTaskUpdated();
            }
          }} 
        />
      </Box>
    </Paper>
  );
};

export default TaskActions; 